import { Link } from 'react-router';
import type { ImageModel } from '@rocco/types/image-model';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { TileOverlayWithFollowSelector } from '~/modules/moodboards/components';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import { buildCdnResizeUrl } from '~/utils/image-cdn-resize';

interface EditorsChoiceProductTileProps {
    product: {
        id: number;
        title: string;
        link: string;
        images: ImageModel[];
        professional: { name: string };
    };
}

/**
 * Simplified product tile for the Featured in Curated Collection section.
 * Displays a "FEATURED" badge on each product tile.
 */
export const EditorsChoiceProductTile = ({
    product,
}: EditorsChoiceProductTileProps) => {
    const primaryImage = product.images[0];
    const fit = primaryImage?.multiplied ? 'contain' : 'cover';
    const resizedSrc = primaryImage
        ? buildCdnResizeUrl(primaryImage.src, {
              width: 440,
              height: 582,
              fit,
          })
        : undefined;

    return (
        <Link
            to={product.link}
            prefetch="intent"
            className="group flex flex-col gap-3.75"
        >
            {/* Image container */}
            <div
                className={cn(
                    'relative aspect-440/582 w-full overflow-hidden rounded-lg',
                    'bg-[#FAFAFA]',
                )}
            >
                {primaryImage && (
                    <Image
                        src={primaryImage.src}
                        overrideSrc={resizedSrc}
                        alt={primaryImage.alt || product.title}
                        fill={true}
                        fit={fit}
                        multiplied={primaryImage.multiplied}
                        className="transition-transform duration-300 group-hover:scale-105"
                    />
                )}

                {/* FEATURED badge */}
                <div className="absolute top-5 left-5 flex h-6.5 items-center justify-center rounded-full bg-[#f2f2f2] px-4 transition-opacity duration-300 group-hover:opacity-0">
                    <span className="text-[0.625rem] leading-[0.625rem] font-medium tracking-[0.8px] text-[#141414] uppercase">
                        featured
                    </span>
                </div>

                {/* Save overlay — Pinterest-style board dropdown + Save/Saved button */}
                <TileOverlayWithFollowSelector
                    pinId={product.id}
                    pinType="ProductPin"
                />
            </div>

            {/* Product info */}
            <div className="flex flex-col gap-[2px]">
                <TextTooltip content={product.title}>
                    <p className="line-clamp-1 text-base leading-[1.3] font-medium tracking-[-0.02rem] text-[#141414]">
                        {product.title}
                    </p>
                </TextTooltip>
                <TextTooltip content={product.professional.name}>
                    <p className="line-clamp-1 text-[0.9375rem] leading-[1.3] font-medium tracking-[-0.02rem] text-[#141414]">
                        {product.professional.name}
                    </p>
                </TextTooltip>
            </div>
        </Link>
    );
};
